import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useMedia } from 'react-use'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import ParallaxImage from '@/components/base/parallax-image'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { videoPlayerRevealAnimation } from '@/components/sections/animations/video-player'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type VideoPlayerProps = HTMLAttributes<HTMLDivElement> &
  Partial<{ media: WPMedia; title: string; text: string }>

const VideoPlayer = ({ title, text, media, ...props }: VideoPlayerProps) => {
  const { strings } = useStore()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const isSM = useMedia('(max-width: 767px)', true)

  const posterSource =
    isSM && media?.poster_mobile?.url ? media?.poster_mobile : media?.poster
  const videoSource =
    isSM && media?.video_mobile?.url ? media?.video_mobile : media?.video

  useEffect(() => {
    if (!videoRef.current) return
    if (isPlaying) {
      videoRef.current.play()
    } else {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }, [isPlaying])

  return (
    <Motion animation={videoPlayerRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Spacer variant="xl" />
          <Container>
            <Grid className="text-white">
              <div className="relative col-span-2 aspect-auto min-h-[500px] md:col-span-6 md:aspect-video md:min-h-0 lg:col-span-12">
                <div
                  className={cn(
                    'absolute left-0 top-0 z-10 flex size-full items-center justify-center',
                    'after:bg-dark/50 after:absolute after:left-0 after:top-0 after:size-full'
                  )}
                  data-motion-image
                >
                  <ParallaxImage
                    offset={-20}
                    className="absolute inset-0 h-full w-full"
                  >
                    <>
                      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-[black]/[0.2]" />
                      <img
                        src={posterSource?.url || ''}
                        alt={posterSource?.alt || ''}
                        className="absolute inset-0 z-10 size-full object-cover"
                      />
                    </>
                  </ParallaxImage>

                  <button
                    aria-label={
                      strings?.labels?.play_video || 'Riproduci video'
                    }
                    className="relative z-20 flex size-[60px] cursor-pointer items-center justify-center rounded-radius-minimal bg-[white]/10 text-[white] backdrop-blur-[17px] md:size-[72px]"
                    onClick={() => {
                      setIsPlaying(true)
                    }}
                    data-motion-cta
                  >
                    <SVGIcon
                      symbol="play"
                      className="size-[16px]"
                      aria-hidden="true"
                    />
                  </button>
                </div>

                <div
                  className={cn(
                    'pointer-events-none absolute inset-0 z-30 opacity-0',
                    'transition-opacity duration-700 ease-out-cubic',
                    {
                      'pointer-events-auto opacity-100': isPlaying
                    }
                  )}
                >
                  <video
                    ref={videoRef}
                    src={videoSource?.url}
                    controls
                    playsInline
                    className="absolute z-10 h-full w-full object-cover"
                    onEnded={() => {
                      setIsPlaying(false)
                    }}
                  />
                </div>

                <div className="dark pointer-events-none absolute inset-0 z-10 flex flex-col gap-x-padding-md gap-y-spacing-sm px-padding-sm py-padding-md md:flex-row md:px-padding-lg md:py-padding-md">
                  <Typography
                    variant="h3"
                    component="h3"
                    className="flex-[0_0_auto] text-s-h5 md:flex-[0_0_calc(50%-2rem)] md:text-s-h3"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                  <Typography
                    variant="regular"
                    component="p"
                    className="flex-[0_0_auto] text-text-primary-light md:flex-[0_0_calc(50%-2rem)] dark:text-text-primary-dark"
                    data-motion-text
                  >
                    {text}
                  </Typography>
                </div>
              </div>
            </Grid>
          </Container>
          <Spacer variant="xl" />
        </Section>
      )}
    </Motion>
  )
}

export default VideoPlayer
